import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { ConfidenceBadgeComponent } from "../confidence-badge/confidence-badge.component";
import { aggregatePlanTaskStatus, planTaskStatusVisual } from "../plan-task-status";
import type { PlanStatusTone, PlanTaskState } from "../plan-task-status";

/** One task row of a grouped plan step: display text, live state and attempt count. */
export interface PlanStepListTask {
  text: string;
  state: PlanTaskState;
  attempts?: number | null;
  /** True when the task is parked on a `frontend_tools` interrupt (the browser runs it, not the user). */
  frontendTool?: boolean;
}

/**
 * One grouped plan step block (plan-cards-spec §Pasos agrupados): step text, optional muted
 * meta line, optional confidence and its task rows. The visual step state is ALWAYS the
 * aggregate of its own task states — never a sequence position.
 */
export interface PlanStepListStep {
  text: string;
  meta?: string | null;
  confidence?: number | null;
  tasks: PlanStepListTask[];
}

/** Render model of one task row: icon copy, tone class and the attempt indicator (> 1 only). */
interface PlanStepListTaskView {
  task: PlanStepListTask;
  icon: string;
  toneClass: string;
  /** Human state word — the status icon tooltip (state words never render as row text). */
  tooltip: string;
  /** Attempt count of the indicator; `null` hides the indicator (attempts ≤ 1). */
  attempts: number | null;
  /** Native title tooltip of the attempt indicator ("N attempts"). */
  attemptsTooltip: string;
}

/** Render model of one step block: header copy plus the aggregated step-state icon. */
interface PlanStepListStepView {
  step: PlanStepListStep;
  /** Circle number = index + 1 — identifier only, never a sequence indicator. */
  number: number;
  /** True when the confidence badge renders (toggle on AND a confidence value present). */
  showBadge: boolean;
  /** Normalized confidence (undefined folded to null) passed to the badge input. */
  confidence: number | null;
  aggregateIcon: string;
  aggregateToneClass: string;
  /** Human aggregate state word — the step icon tooltip. */
  aggregateTooltip: string;
  tasks: PlanStepListTaskView[];
}

/** Token-backed color class per semantic tone — see the component SCSS `.tone-*` rules. */
const TONE_CLASSES: Readonly<Record<PlanStatusTone, string>> = {
  ok: "tone-ok",
  err: "tone-err",
  warn: "tone-warn",
  idle: "tone-idle",
  pri: "tone-pri",
};

/**
 * Shared grouped steps block of the plan card surfaces (plan-cards-spec §Pasos agrupados):
 * bordered step blocks (numbered circle, step text, meta) with a right rail stacking the
 * confidence badge over the aggregated step-state icon, and divider-separated task rows each
 * with its own state icon and attempt indicator. Execution is order-independent, so the circle
 * number is an identifier only and the step state derives solely from its own tasks' states.
 * The same block renders in the plan card (with confidence) and in plan progress (without).
 */
@Component({
  selector: "app-plan-step-list",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatTooltip, ConfidenceBadgeComponent],
  templateUrl: "./plan-step-list.component.html",
  styleUrl: "./plan-step-list.component.scss",
})
export class PlanStepListComponent {
  /** Grouped steps of the plan in display order — each renders as an independent bordered block. */
  readonly steps = input.required<readonly PlanStepListStep[]>();

  /** Toggles the per-step confidence badge (plan progress reuses the list without it). */
  readonly showConfidence = input<boolean>(true);

  /**
   * Derived render model — the step state is the aggregate of its own task states
   * (FAILED > all DONE > any waiting > any READY > PENDING); `confidence ?? null` and the
   * attempt indicator are normalized here so the template stays nullable-free.
   */
  protected readonly viewSteps = computed<PlanStepListStepView[]>(() =>
    this.steps().map((step, index) => {
      const aggregateState = aggregatePlanTaskStatus(step.tasks.map((task) => task.state));
      const waitingTasks = step.tasks.filter(
        (task) => task.state === "WAITING_USER" || task.state === "WAITING_INPUT" || task.state === "WAITING",
      );
      const aggregate = planTaskStatusVisual(
        aggregateState,
        waitingTasks.length > 0 && waitingTasks.every((task) => task.frontendTool === true),
      );
      const confidence = step.confidence ?? null;
      return {
        step,
        number: index + 1,
        showBadge: this.showConfidence() && confidence !== null,
        confidence,
        aggregateIcon: aggregate.icon,
        aggregateToneClass: TONE_CLASSES[aggregate.tone],
        aggregateTooltip: aggregate.label,
        tasks: step.tasks.map((task) => {
          const visual = planTaskStatusVisual(task.state, task.frontendTool === true);
          const attempts = typeof task.attempts === "number" && task.attempts > 1 ? task.attempts : null;
          return {
            task,
            icon: visual.icon,
            toneClass: TONE_CLASSES[visual.tone],
            tooltip: visual.label,
            attempts,
            attemptsTooltip: attempts === null ? "" : `${attempts} attempts`,
          };
        }),
      };
    }),
  );
}
